/**
 * NB Wingfoil Landing Page
 *
 * This component is temporaly until the new booking flow is implemented.
 */

import ServiceCard from '@/components/common/cards/service-card/ServiceCard';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { TOrganizationFAQ } from '@/services/api/organization-api';

import { Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PaymentStep from '../payment-step/PaymentStep';
import { BookingByGroups } from '@/components/common/forms/booking-datepicker-form-body/Groups';
import useMixPanel from '@/hooks/useMixPanel';
import ParticipantWingfoil from '@/components/common/forms/participants-data-form-body/ParticipantWingfoil';
import { useFormLandingWingfoil } from '@/hooks/business-logic/nb-campus/useFormLandingWingfoil';
import { TUserData } from '@/services/api/user-api';
import { Service } from '@/services/api/services-api';
import { RegisteredUserModal } from '@/components/common/modals/registered-user-modal/RegisteredUserModal';
import { TiptapViewer } from '@/components/common/tiptap-viewer/TiptapViewer';

type Props = {
  organization: TOrganizationFAQ;
  user: TUserData | undefined;
  service: Service;
};

const NBLandingByDivisionGroupsWingfoil = ({
  organization,
  user,
  service
}: Props) => {
  const Mixpanel = useMixPanel();
  const { t } = useTranslation(['newBooking', 'common']);
  const {
    form,
    isPendingSale,
    isSaleCreated,
    onSubmit,
    handleBlur,
    emailHint,
    isOpenRegisteredUserModal,
    setIsOpenRegisteredUserModal
  } = useFormLandingWingfoil({
    service,
    user
  });

  if (isPendingSale) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (isSaleCreated) {
    return <PaymentStep organization={organization} />;
  }

  const isTiptapViewer = service.landing_page_content;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {service && (
          <div className="flex flex-col gap-10">
            <RegisteredUserModal
              isOpenAlertDialog={isOpenRegisteredUserModal}
              setIsOpenAlertDialog={setIsOpenRegisteredUserModal}
              organization={organization}
              email={emailHint}
              isLanding
              serviceId={service.id}
            />
            {isTiptapViewer ? (
              <TiptapViewer
                content={service.landing_page_content}
                imageUrl={service.imageUrl}
                className="prose prose-lg max-w-none"
              />
            ) : (
              <ServiceCard service={service} organization={organization} />
            )}
            <BookingByGroups
              form={form}
              organization={organization}
              tz={organization.tz}
              service={service}
              isLanding
              isMultiple={false}
            />
            <ParticipantWingfoil
              form={form}
              user={user}
              handleBlur={handleBlur}
            />
            <Button
              type="submit"
              className="w-full"
              disabled={isPendingSale}
              onClick={() =>
                Mixpanel.track('Press continue button', {
                  screen: `[${service?.name}]: New Booking Landing`,
                  Activity: service.name
                })
              }
            >
              {isPendingSale ? (
                <Loader className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                t('stepParticipants.form.continue')
              )}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
};

export default NBLandingByDivisionGroupsWingfoil;
